import { serviceItemsEn, serviceItemsEs } from './serviceItems';

export type Language = 'en' | 'es';

export const translations = {
  en: {
    nav: {
      services: 'Services',
      about: 'About',
      contact: 'Contact',
      menu: 'Menu',
    },
    hero: {
      eyebrow: 'Founder-led software systems',
      titleLines: ['Software', 'built to', 'fit your', 'business.'],
      titleAccentIndex: 3,
      subtitle:
        'Soltec replaces internal systems with custom software, automation, and AI tools that your business actually needs.',
      ctaPrimary: 'Start a project',
      ctaSecondary: 'View services',
    },
    services: {
      page: {
        eyebrow: 'What we build',
        title: 'Services built for outgrowing businesses that want to take a quality leap.',
        intro:
          'We concentrate in custom software on modern and reliable infrastructure. Plus web tools, audits, and automation to accelerate the growth.',
        serviceLabel: 'Service',
        backToList: 'All services',
        deliverables: 'What you get',
        openService: 'Open service',
        startProject: 'Start a project',
        nextService: 'Next service',
        prevService: 'Previous service',
      },
      preview: {
        eyebrow: 'Selected services',
        titleLine1: 'Services provided to',
        titleLine2: 'hold up in use.',
        subtitle:
          'From custom software to audits and automation. Solutions designed around how your business actually operates and needs.',
        viewAll: 'View all services',
        openService: 'View service',
        carouselPrev: 'Previous service',
        carouselNext: 'Next service',
      },
      items: serviceItemsEn,
    },
    about: {
      eyebrow: 'The team',
      titleLine1: 'Experts who build',
      titleLine2: 'reliable systems.',
      intro:
        'We specialize in internal software for companies that have outgrown their current tools — modern, self-hosted infrastructure the client actually owns.',
      team: [
        {
          name: 'Francesco Solari',
          role: 'Software Developer',
          description:
            'Full-Stack Developer with over 4 years of industry experience. Has worked with multinational companies while studying in Quebec, Canada, gaining valuable experience alongside top professionals and mentors.',
        },
        {
          name: 'Fabio Palomino',
          role: 'AI & Automation Specialist',
          description:
            'Strong expertise in Machine Learning, Deep Learning, and LLMs. Specialized in finance, actuarial science, and banking, delivering advanced solutions for small and medium-sized businesses.',
        },
      ],
    },
    contact: {
      eyebrow: 'Contact',
      titleLine1: 'Tell us about',
      titleLine2: 'your project.',
      subtitle:
        'Share a few details and we will follow up with next steps. Set your budget range and preferred currency so we can scope the conversation.',
      bilingualNote:
        'We deliver every service in English and Spanish. Wherever you are in the world, you can work with SOLTEC in the language you feel most comfortable with.',
      name: 'Name',
      email: 'Email',
      company: 'Company',
      message: 'Project details',
      namePlaceholder: 'Your full name',
      emailPlaceholder: 'you@email.com',
      companyPlaceholder: 'Company or project name',
      messagePlaceholder: 'What are you trying to build or fix?',
      formSectionYou: 'About you',
      formSectionBudget: 'Investment range',
      formSectionProject: 'Description',
      budgetLabel: 'Budget range',
      budgetCurrencyLabel: 'Currency',
      budgetMinLabel: 'Minimum',
      budgetMaxLabel: 'Maximum',
      budgetLetsTalk: "Let's talk",
      budgetUpTo: 'up to',
      budgetRangeError: 'Minimum budget cannot be higher than maximum.',
      budgetCurrencies: [
        { value: 'USD', label: 'USD' },
        { value: 'CAD', label: 'CAD' },
        { value: 'PEN', label: 'PEN' },
      ],
      submit: 'Send message',
      submitting: 'Sending...',
      submitSuccess: 'Message sent! We will get back to you soon.',
      submitError: 'Something went wrong. Please try again.',
      mailSubject: 'Project inquiry from {{name}}',
      mailBodyName: 'Name',
      mailBodyEmail: 'Email',
      mailBodyCompany: 'Company',
      mailBodyBudget: 'Budget',
      mailBodyMessage: 'Details',
    },
    footer: {
      services: 'Services',
      about: 'About',
      contact: 'Contact',
      privacy: 'Privacy',
      security: 'Security',
      terms: 'Terms',
      location: 'Quebec, Canada',
      copyright: 'All rights reserved.',
    },
    language: {
      switchTo: 'Switch to {{language}}',
      en: 'English',
      es: 'Spanish',
    },
    theme: {
      switchTo: 'Switch to {{mode}} mode',
      light: 'light',
      dark: 'dark',
    },
  },
  es: {
    nav: {
      services: 'Servicios',
      about: 'Nosotros',
      contact: 'Contacto',
      menu: 'Menú',
    },
    hero: {
      eyebrow: 'Sistemas de software liderados por expertos',
      titleLines: ['Software', 'construido para', 'encajar en tu', 'negocio.'],
      titleAccentIndex: 3,
      subtitle:
        'Soltec reemplaza sistemas internos con software a medida, automatización e IA que tu negocio realmente necesita.',
      ctaPrimary: 'Iniciar un proyecto',
      ctaSecondary: 'Ver servicios',
    },
    services: {
      page: {
        eyebrow: 'Lo que construimos',
        title: 'Servicios diseñados a empresas emergentes que quieren dar un salto de calidad.',
        intro:
          'Nos especializamos en software interno a medida sobre infraestructura moderna y fiable. Además de herramientas web, auditorías y automatización para acelerar el crecimiento.',
        serviceLabel: 'Servicio',
        backToList: 'Todos los servicios',
        deliverables: 'Lo que obtienes',
        openService: 'Ver servicio',
        startProject: 'Iniciar un proyecto',
        nextService: 'Siguiente servicio',
        prevService: 'Servicio anterior',
      },
      preview: {
        eyebrow: 'Servicios destacados',
        titleLine1: 'Servicios proporcionados para',
        titleLine2: 'aguantar en uso real.',
        subtitle:
          'Desde software interno hasta auditorías y automatización — soluciones diseñadas alrededor de cómo opera realmente tu negocio.',
        viewAll: 'Ver todos los servicios',
        openService: 'Ver servicio',
        carouselPrev: 'Servicio anterior',
        carouselNext: 'Siguiente servicio',
      },
      items: serviceItemsEs,
    },
    about: {
      eyebrow: 'El equipo',
      titleLine1: 'Expertos que construyen',
      titleLine2: 'sistemas fiables.',
      intro:
        'Nos especializamos en software interno para empresas que han superado sus herramientas actuales — infraestructura moderna autohospedada que el cliente realmente posee.',
      team: [
        {
          name: 'Francesco Solari',
          role: 'Desarrollador de Software',
          description:
            'Desarrollador Full-Stack con más de 4 años de experiencia en el sector. Ha colaborado con empresas transnacionales, efectuando sus estudios en Quebec, Canadá. Adquiriendo un valioso aprendizaje junto a grandes profesionales y mentores de la industria.',
        },
        {
          name: 'Fabio Palomino',
          role: 'Especialista en IA y Automatización',
          description:
            'Con un sólido dominio en Machine Learning, Deep Learning y LLMs. Cuenta con una basta especialización en finanzas, ciencias actuariales y banca. Brindando soluciones avanzadas para medianas y pequeñas empresas.',
        },
      ],
    },
    contact: {
      eyebrow: 'Contacto',
      titleLine1: 'Cuéntanos sobre',
      titleLine2: 'tu proyecto.',
      subtitle:
        'Comparte algunos detalles y te responderemos con los próximos pasos. Indica tu rango de presupuesto y moneda preferida para enfocar la conversación.',
      bilingualNote:
        'Ofrecemos la totalidad de nuestros servicios en inglés y español. Dondequiera que estés, puedes trabajar con SOLTEC en el idioma con el que te sientas más cómodo.',
      name: 'Nombre',
      email: 'Correo electrónico',
      company: 'Empresa',
      message: 'Detalles del proyecto',
      namePlaceholder: 'Tu nombre completo',
      emailPlaceholder: 'tu@email.com',
      companyPlaceholder: 'Empresa o nombre del proyecto',
      messagePlaceholder: '¿Qué quieres construir o resolver?',
      formSectionYou: 'Sobre ti',
      formSectionBudget: 'Rango de inversión',
      formSectionProject: 'Descripción',
      budgetLabel: 'Rango de presupuesto',
      budgetCurrencyLabel: 'Moneda',
      budgetMinLabel: 'Mínimo',
      budgetMaxLabel: 'Máximo',
      budgetLetsTalk: 'Hablemos',
      budgetUpTo: 'hasta',
      budgetRangeError: 'El presupuesto mínimo no puede ser mayor que el máximo.',
      budgetCurrencies: [
        { value: 'USD', label: 'USD' },
        { value: 'CAD', label: 'CAD' },
        { value: 'PEN', label: 'PEN' },
      ],
      submit: 'Enviar mensaje',
      submitting: 'Enviando...',
      submitSuccess: '¡Mensaje enviado! Te responderemos pronto.',
      submitError: 'Algo salió mal. Intenta de nuevo.',
      mailSubject: 'Consulta de proyecto de {{name}}',
      mailBodyName: 'Nombre',
      mailBodyEmail: 'Email',
      mailBodyCompany: 'Empresa',
      mailBodyBudget: 'Presupuesto',
      mailBodyMessage: 'Detalles',
    },
    footer: {
      services: 'Servicios',
      about: 'Nosotros',
      contact: 'Contacto',
      privacy: 'Privacidad',
      security: 'Seguridad',
      terms: 'Términos',
      location: 'Quebec, Canada',
      copyright: 'Todos los derechos reservados.',
    },
    language: {
      switchTo: 'Cambiar a {{language}}',
      en: 'Inglés',
      es: 'Español',
    },
    theme: {
      switchTo: 'Cambiar a modo {{mode}}',
      light: 'claro',
      dark: 'oscuro',
    },
  },
};

export type TranslationKeys = (typeof translations)[Language];
